
import { useState, useEffect } from "react";
import { useDropzone } from 'react-dropzone';
import { ExternalLink, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ReceiptUploaderProps {
  paymentId: string;
  currentReceiptUrl: string | null;
  onUploadSuccess: (newUrl: string) => void;
}

export const ReceiptUploader = ({ 
  paymentId, 
  currentReceiptUrl, 
  onUploadSuccess 
}: ReceiptUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'pdf' | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (currentReceiptUrl) {
      setPreviewUrl(currentReceiptUrl);
      const isPDF = currentReceiptUrl.toLowerCase().endsWith('.pdf');
      setFileType(isPDF ? 'pdf' : 'image');
      setLoadError(false);
    }
  }, [currentReceiptUrl]);

  useEffect(() => {
    return () => {
      // Cleanup object URLs only for locally created previews
      if (previewUrl && !previewUrl.includes('supabase.co')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('payment-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('payment-photos')
        .getPublicUrl(filePath);

      // Update payment with new receipt URL and set status to paid
      const { error: updateError } = await supabase
        .from('payments')
        .update({ 
          receipt_url: publicUrl,
          status: 'paid' // Automatically change status to paid
        })
        .eq('id', paymentId);

      if (updateError) throw updateError;

      // Update local state
      setPreviewUrl(publicUrl);
      onUploadSuccess(publicUrl);
      
      toast({
        title: "Éxito",
        description: "Comprobante subido y pago actualizado a 'pagado'",
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: "Error al subir el comprobante",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setLoadError(false);
      setFileType(file.type.includes('pdf') ? 'pdf' : 'image');
      
      // Automatically upload file when dropped
      await handleFileUpload(file);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
      'application/pdf': []
    },
    maxFiles: 1,
    multiple: false
  });

  const handleLoadError = () => {
    setLoadError(true);
    console.error('Error loading file:', currentReceiptUrl);
  };

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors
        ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}
        ${isUploading ? 'opacity-50' : ''}`}
    >
      <input {...getInputProps()} />
      <div className="text-center space-y-3">
        {previewUrl && !loadError ? (
          <div className="space-y-2">
            {fileType === 'image' ? (
              <div className="space-y-2">
                <img
                  src={previewUrl}
                  alt="Comprobante de pago"
                  className="max-h-32 mx-auto object-contain"
                  onError={handleLoadError}
                />
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <a 
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver imagen subida
                  </a>
                </Button>
              </div>
            ) : fileType === 'pdf' ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="w-8 h-8 text-muted-foreground" />
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <a 
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ver PDF
                  </a>
                </Button>
              </div>
            ) : null}
            <p className="text-sm text-muted-foreground">
              {selectedFile ? selectedFile.name : 'Comprobante guardado'}
            </p>
            <p className="text-xs text-muted-foreground italic">
              Haz clic o arrastra un nuevo archivo para reemplazar el comprobante
            </p>
          </div>
        ) : (
          <>
            <div className="text-muted-foreground">
              {isDragActive ? (
                <p>Suelta el archivo aquí ...</p>
              ) : (
                <p>Arrastra un archivo o haz clic para seleccionar</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Archivos permitidos: imágenes (PNG, JPG, GIF) y PDF
            </p>
          </>
        )}
        {isUploading && <p className="text-sm text-muted-foreground">Subiendo...</p>}
      </div>
    </div>
  );
};
